import { createBrowserRouter, Navigate } from "react-router-dom";
import { AppLayout } from "@/components/layout/app-layout";
import { RouteErrorScreen } from "@/components/errors/RouteErrorScreen";
import { LoginPage } from "@/pages/login";
import { RegisterPage } from "@/pages/register";
import { ActivatePage } from "@/pages/activate";
import { AuthCallbackPage } from "@/pages/auth-callback";
import { InboxPage } from "@/pages/inbox";
import { EpicsPage } from "@/pages/epics/index";
import { EpicDetailPage } from "@/pages/epics/epic-detail";
import { EpicRequestsPage } from "@/pages/epic-requests/index";
import { EpicRequestDetailPage } from "@/pages/epic-requests/epic-request-detail";
import { FeatureDetail } from "@/components/features/feature-detail";
import { TaskDetail } from "@/components/tasks/task-detail";
import { TeamsPage } from "@/pages/teams/index";
import { SettingsPage } from "@/pages/settings";
import { DashboardPage } from "@/pages/dashboard";
import { WhatsNewPage } from "@/pages/whats-new";
import { AdminGuard } from "@/components/guards/AdminGuard";
import { AdminUsersPage } from "@/pages/admin/UsersPage";
import { UserReportPage } from "@/pages/admin/UserReportPage";
import { ActivityDashboardPage } from "@/pages/admin/ActivityDashboardPage";
import { ActivityPage } from "@/pages/activity";
import { NotFoundPage } from "@/pages/not-found";
import { TicketListPage } from "@/pages/fogbugz/tickets";
import { TicketDetailPage } from "@/pages/fogbugz/ticket-detail";
import { FogBugzFormulatorPage } from "@/pages/fogbugz/formulator";
import { RequireAdoPat } from "@/components/ado/require-ado-pat";
import { AdoMyItemsPage } from "@/pages/ado/my-items";
import { AdoItemDetailPage } from "@/pages/ado/item-detail";
import { ToolboxHomePage } from "@/pages/toolbox/home";
import { ToolboxSetupPage } from "@/pages/toolbox/setup";
import { ToolboxFormulatorPage } from "@/pages/toolbox/formulator";
import { ToolboxRolesPage } from "@/pages/toolbox/roles";
import { ToolboxDownloadPage } from "@/pages/toolbox/download";
import { ToolboxHelpInboxPage } from "@/pages/toolbox/help-inbox";
import { ToolboxFogbugzEvtxPage } from "@/pages/toolbox/fogbugz-evtx";
import { ToolboxFogbugzFormulatorPage } from "@/pages/toolbox/fogbugz-formulator";
import { ToolboxFogbugzToAdoPage } from "@/pages/toolbox/fogbugz-to-ado";
import { RequireFogbugzToolbox } from "@/components/toolbox/require-fogbugz-toolbox";
import { RequireAskToolbox } from "@/components/toolbox/require-ask-toolbox";
import { ToolboxAskPage } from "@/pages/toolbox/ask";
import { RequireToolboxCapability } from "@/components/toolbox/require-capability";
import { groomingRoutes } from "@/pages/grooming/routes";

import { ErrorScreen } from "@/components/errors/ErrorScreen";
import { FOGBUGZ_FORMULATOR_TOOL_NAME, FOGBUGZ_TO_ADO_TOOL_NAME, TOOLBOX_HOME_PATH } from "@/lib/toolbox/tools";
import { TOOLBOX_DOWNLOAD_PAGE_PATH } from "@ttc-spectree/toolbox-contract/contracts";

export const router = createBrowserRouter([
  // Preview routes — dev only, tree-shaken in production builds
  ...(import.meta.env.DEV
    ? [
        {
          path: "/preview/error",
          element: (
            <ErrorScreen
              error={Object.assign(new Error("Something went wrong while loading the dashboard component. This is a preview of the error experience."), { name: "RenderError" })}
              statusCode={500}
            />
          ),
        },
        {
          path: "/preview/404",
          element: <NotFoundPage />,
        },
      ]
    : []),
  {
    path: "/login",
    element: <LoginPage />,
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/register",
    element: <RegisterPage />,
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/activate",
    element: <ActivatePage />,
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/auth/callback",
    element: <AuthCallbackPage />,
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/",
    element: <AppLayout />,
    errorElement: <RouteErrorScreen />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: "inbox", element: <InboxPage /> },
      { path: "dashboard", element: <DashboardPage /> },
      { path: "activity", element: <ActivityPage /> },
      { path: "whats-new", element: <WhatsNewPage /> },
      { path: "epic-requests", element: <EpicRequestsPage /> },
      { path: "epic-requests/:requestId", element: <EpicRequestDetailPage /> },
      { path: "epics", element: <EpicsPage /> },
      {
        path: "epics/:epicId",
        element: <EpicDetailPage />,
      },
      {
        path: "features/:featureId",
        element: <FeatureDetail />,
      },
      {
        path: "tasks/:taskId",
        element: <TaskDetail />,
      },
      { path: "teams", element: <TeamsPage /> },
      { path: "teams/:teamId", element: <TeamsPage /> },
      { path: "settings", element: <SettingsPage /> },
      { path: "fogbugz/formulator", element: <FogBugzFormulatorPage /> },
      { path: "fogbugz/tickets", element: <TicketListPage /> },
      { path: "fogbugz/tickets/:id", element: <TicketDetailPage /> },
      { path: "ado", element: <Navigate to="/ado/items" replace /> },
      { path: "ado/items", element: <RequireAdoPat><AdoMyItemsPage /></RequireAdoPat> },
      { path: "ado/items/:project/:id", element: <RequireAdoPat><AdoItemDetailPage /></RequireAdoPat> },
      // SpecTree Toolbox (PI-SPEC-SW-1395): the desktop app opens /toolbox; both pages also render in a browser.
      // The Toolbox shell pages are open without groomingVisible (E282, E283): they host more than
      // one tool now (Formulator beside Grooming). Only the Grooming entries keep RequireGroomingVisible,
      // in pages/grooming/routes.tsx and on the Grooming tile/switcher entries.
      { path: "toolbox", element: <ToolboxHomePage /> },
      { path: "toolbox/setup", element: <ToolboxSetupPage /> },
      // Toolbox download (PI-SPEC-SW-1468, UX-PM-41): sign-in required (AppLayout). The unsigned pilot's
      // installers from the update feed, with the SmartScreen step; the in-app update band links here.
      { path: TOOLBOX_DOWNLOAD_PAGE_PATH.replace(/^\//, ""), element: <ToolboxDownloadPage /> },
      // Toolbox Formulator (PI-SPEC-SW-1418): runs only inside SpecTree Toolbox (a browser shows how to get it).
      // Needs an ADO PAT like the other ADO pages; not behind groomingVisible (the page follows the formulator capability).
      { path: "toolbox/formulator", element: <RequireAdoPat><ToolboxFormulatorPage /></RequireAdoPat> },
      // Toolbox FogBugz event log summary (PI-SPEC-SW-1598, ER-1663): the `fogbugz` capability on any project
      // AND the person's saved FogBugz key (no ADO PAT). The summary is written by local Claude, so the page runs
      // only inside SpecTree Toolbox (a browser shows how to get it). The website's /fogbugz pages are unchanged.
      {
        path: "toolbox/fogbugz",
        element: (
          <RequireFogbugzToolbox>
            <ToolboxFogbugzEvtxPage />
          </RequireFogbugzToolbox>
        ),
      },
      // Toolbox FogBugz Formulator (PI-SPEC-SW-1620, ER-1665): the same gate as the event log summary (the
      // `fogbugz` capability on any project AND the person's saved FogBugz key). The interview runs on local
      // Claude, so the page runs only inside SpecTree Toolbox. The website's /fogbugz/formulator is unchanged.
      {
        path: "toolbox/fogbugz-formulator",
        element: (
          <RequireFogbugzToolbox
            toolName={FOGBUGZ_FORMULATOR_TOOL_NAME}
            keyReason="files the case as you"
          >
            <ToolboxFogbugzFormulatorPage />
          </RequireFogbugzToolbox>
        ),
      },
      // Toolbox FogBugz to ADO (PI-SPEC-SW-1640, ER-1666 Package E): the `fogbugz-to-ado` capability on any project
      // (Admins and Project Managers) AND the person's saved FogBugz key (the case is read and the note posted as them),
      // then an ADO PAT (the Bug is created as them; RequireAdoPat sends to Setup's "Connect Azure DevOps" and back).
      // Claude reads the case and drafts the Bug on the person's computer, so the page runs only inside SpecTree Toolbox.
      {
        path: "toolbox/fogbugz-to-ado",
        element: (
          <RequireFogbugzToolbox
            capability="fogbugz-to-ado"
            toolName={FOGBUGZ_TO_ADO_TOOL_NAME}
            keyReason="reads the case and posts the ADO link to it as you"
            noRoleBody="FogBugz to ADO is for Project Managers and Admins. Ask an Admin if you need it."
          >
            <RequireAdoPat>
              <ToolboxFogbugzToAdoPage />
            </RequireAdoPat>
          </RequireFogbugzToolbox>
        ),
      },
      // Toolbox Product Q&A (PI-SPEC-SW-1628, ER-1667 Package F): the `ask` capability on any project (no saved
      // key; the page checks each product's code on this computer). Answers come from local Claude, so the page runs
      // only inside SpecTree Toolbox (a browser shows how to get it). Only the product list is read from SpecTree.
      {
        path: "toolbox/ask",
        element: (
          <RequireAskToolbox>
            <ToolboxAskPage />
          </RequireAskToolbox>
        ),
      },
      // Toolbox roles (PI-SPEC-SW-1431): Admins and Project Managers manage per-project roles. Behind assign-roles on
      // any project (an Admin everywhere); renders in a browser and in SpecTree Toolbox. The API enforces every change.
      {
        path: "toolbox/roles",
        element: (
          <RequireToolboxCapability capability="assign-roles" anywhere redirectTo={TOOLBOX_HOME_PATH}>
            <ToolboxRolesPage />
          </RequireToolboxCapability>
        ),
      },
      // Toolbox Help inbox (PI-SPEC-SW-1559, ER-1655 Resolved Decision 5): what people sent from Help with
      // "Send to Aaron". Global Admins only (the page redirects anyone else; the API refuses them); renders in
      // SpecTree Toolbox and in a browser.
      { path: "toolbox/help-inbox", element: <ToolboxHelpInboxPage /> },
      // SpecTree Grooming route map v2 (PI-SPEC-SW-1399): items/:id is the backlog's reading pane,
      // session and session/:batchId the Session screen, agenda/:batchId the shared agenda (sign-in,
      // no ADO PAT). Every route keeps RequireGroomingVisible (a Toolbox role). See pages/grooming/routes.tsx.
      ...groomingRoutes,
      {
        path: "admin",
        element: <AdminGuard />,
        children: [
          { index: true, element: <Navigate to="/admin/users" replace /> },
          { path: "users", element: <AdminUsersPage /> },
          { path: "activity/user/:userId", element: <UserReportPage /> },
          { path: "activity", element: <ActivityDashboardPage /> },
        ],
      },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);
